import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  Divider,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  startRegistration,
  browserSupportsWebAuthn
} from '@simplewebauthn/browser';
import { useThemeMode } from '../../contexts/ThemeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const STEPS = ['Account', 'Security', 'Verify Email', 'Approval'];

const Signup = () => {
  const { mode } = useThemeMode();
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 0 — credentials
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '', firstName: '', lastName: '' });
  const [signupToken, setSignupToken] = useState(null);

  // Step 1 — MFA enrollment
  const [mfaMethod, setMfaMethod] = useState('totp');
  const [totpQr, setTotpQr] = useState(null);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaDone, setMfaDone] = useState(false);

  // Step 2 — email verification
  const [emailCode, setEmailCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Step 3 — pending approval
  const [approved, setApproved] = useState(false);
  const pollRef = useRef(null);

  const webauthnSupported = browserSupportsWebAuthn();

  const authHeaders = (token) => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  });

  // ---- Step 0: create account ----
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim()
        })
      });
      const data = await resp.json();
      if (data.success && data.signupToken) {
        setSignupToken(data.signupToken);
        setStep(1);
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---- Step 1: MFA enrollment ----
  const startTotpSetup = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/totp/setup`, {
        method: 'POST',
        headers: authHeaders(signupToken)
      });
      const data = await resp.json();
      if (data.success) {
        setTotpQr(data.qrCode);
        setTotpSecret(data.secret);
      } else {
        setError(data.error || 'Failed to start authenticator setup');
      }
    } catch (err) {
      setError(err.message || 'Failed to start authenticator setup');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpConfirm = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/totp/confirm`, {
        method: 'POST',
        headers: authHeaders(signupToken),
        body: JSON.stringify({ token: totpCode.trim() })
      });
      const data = await resp.json();
      if (data.success) {
        setMfaDone(true);
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyRegister = async () => {
    setError('');
    setLoading(true);
    try {
      const optResp = await fetch(`${API_BASE_URL}/api/auth/webauthn/register-options`, {
        method: 'POST',
        headers: authHeaders(signupToken)
      });
      const optData = await optResp.json();
      if (!optData.success) {
        setError(optData.error || 'Failed to start passkey registration');
        return;
      }
      const regResp = await startRegistration({ optionsJSON: optData.options });
      const verifyResp = await fetch(`${API_BASE_URL}/api/auth/webauthn/register-verify`, {
        method: 'POST',
        headers: authHeaders(signupToken),
        body: JSON.stringify({ response: regResp, deviceName: 'Primary device' })
      });
      const verifyData = await verifyResp.json();
      if (verifyData.success) {
        setMfaDone(true);
      } else {
        setError(verifyData.error || 'Passkey registration failed');
      }
    } catch (err) {
      setError(err.message || 'Passkey registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- Step 2: email verification ----
  const handleSendEmailCode = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/signup/email-code/send`, {
        method: 'POST',
        headers: authHeaders(signupToken)
      });
      const data = await resp.json();
      if (data.success) {
        setEmailSent(true);
      } else {
        setError(data.error || 'Failed to send code');
      }
    } catch (err) {
      setError(err.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/signup/email-code/verify`, {
        method: 'POST',
        headers: authHeaders(signupToken),
        body: JSON.stringify({ token: emailCode.trim() })
      });
      const data = await resp.json();
      if (data.success) {
        setStep(3);
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- Step 3: poll for approval ----
  useEffect(() => {
    if (step !== 3 || !signupToken) return;
    const poll = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/auth/signup/status`, {
          headers: { 'Authorization': `Bearer ${signupToken}` }
        });
        const data = await resp.json();
        if (data.status === 'active') setApproved(true);
        if (data.status === 'rejected') setError('Your account was not approved.');
      } catch (e) { /* ignore transient poll errors */ }
    };
    pollRef.current = setInterval(poll, 15000);
    poll();
    return () => clearInterval(pollRef.current);
  }, [step, signupToken]);

  // Auto-start TOTP setup when entering step 1 with totp selected
  useEffect(() => {
    if (step === 1 && mfaMethod === 'totp' && !totpQr && !loading) {
      startTotpSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, mfaMethod]);

  const onMfaMethodChange = (_, val) => {
    if (!val || mfaDone) return;
    setMfaMethod(val);
    setError('');
    setTotpQr(null);
    setTotpCode('');
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ width: '100%', maxWidth: 480 }}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  component="img"
                  src={mode === 'dark' ? '/assets/mytradeshub-logo-dark.png' : '/assets/mytradeshub-logo.png'}
                  alt="myTradesHub"
                  sx={{ height: 110, maxWidth: '100%', objectFit: 'contain', mb: 1 }}
                />
                <Typography variant="body1" color="text.secondary">Create your account</Typography>
              </Box>

              <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
                {STEPS.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
              </Stepper>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              {/* STEP 0 — credentials */}
              {step === 0 && (
                <Box component="form" onSubmit={handleRegister}>
                  <TextField fullWidth label="Username" value={form.username} margin="normal" required autoFocus
                    onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))} />
                  <TextField fullWidth label="Email" type="email" value={form.email} margin="normal" required
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField fullWidth label="First name" value={form.firstName} margin="normal"
                      onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))} />
                    <TextField fullWidth label="Last name" value={form.lastName} margin="normal"
                      onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))} />
                  </Box>
                  <TextField fullWidth label="Password" type="password" value={form.password} margin="normal" required
                    helperText="Min 8 chars with upper, lower, number, and special character"
                    onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} />
                  <TextField fullWidth label="Confirm password" type="password" value={form.confirm} margin="normal" required
                    onChange={(e) => setForm(f => ({ ...f, confirm: e.target.value }))} />
                  <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : 'Continue'}
                  </Button>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Already have an account? <a href="/" style={{ color: 'inherit' }}>Sign in</a>
                  </Typography>
                </Box>
              )}

              {/* STEP 1 — MFA enrollment */}
              {step === 1 && (
                <Box>
                  {!mfaDone ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Secure your account — choose a second factor. This is required.
                      </Typography>
                      <ToggleButtonGroup
                        exclusive fullWidth value={mfaMethod} onChange={onMfaMethodChange} sx={{ mb: 2 }}
                      >
                        <ToggleButton value="totp">Authenticator App</ToggleButton>
                        <ToggleButton value="passkey" disabled={!webauthnSupported}>Passkey</ToggleButton>
                      </ToggleButtonGroup>

                      {mfaMethod === 'totp' ? (
                        <Box sx={{ textAlign: 'center' }}>
                          {totpQr ? (
                            <>
                              <img src={totpQr} alt="TOTP QR code" style={{ width: 200, height: 200 }} />
                              <Typography variant="caption" display="block" sx={{ mb: 1, wordBreak: 'break-all' }}>
                                Manual key: {totpSecret}
                              </Typography>
                              <TextField
                                fullWidth label="6-digit code" value={totpCode} margin="normal" autoFocus
                                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                              />
                              <Button fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading || totpCode.length < 6}
                                onClick={handleTotpConfirm}>
                                {loading ? <CircularProgress size={20} /> : 'Verify & Continue'}
                              </Button>
                            </>
                          ) : (
                            <Box sx={{ py: 3 }}>{loading ? <CircularProgress /> : 'Preparing authenticator setup...'}</Box>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Your browser will prompt you to create a passkey (Face ID, Touch ID, Windows Hello, or security key).
                          </Typography>
                          <Button fullWidth variant="contained" disabled={loading} onClick={handlePasskeyRegister}>
                            {loading ? <CircularProgress size={20} /> : 'Create Passkey'}
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {mfaMethod === 'totp' ? 'Authenticator' : 'Passkey'} configured successfully.
                      </Alert>
                      <Button fullWidth variant="contained" onClick={() => { setStep(2); handleSendEmailCode(); }}>
                        Continue to Email Verification
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* STEP 2 — email verification */}
              {step === 2 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    A 6-digit verification code was sent to <b>{form.email}</b>.
                  </Alert>
                  <TextField
                    fullWidth label="Verification code" value={emailCode} margin="normal" autoFocus
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                  />
                  <Button fullWidth variant="contained" sx={{ mt: 2, mb: 1 }} disabled={loading || emailCode.length < 6}
                    onClick={handleVerifyEmailCode}>
                    {loading ? <CircularProgress size={20} /> : 'Verify Email'}
                  </Button>
                  <Button fullWidth variant="text" onClick={handleSendEmailCode} disabled={loading}>
                    {emailSent ? 'Resend code' : 'Send code'}
                  </Button>
                </Box>
              )}

              {/* STEP 3 — pending approval */}
              {step === 3 && (
                <Box sx={{ textAlign: 'center' }}>
                  {approved ? (
                    <>
                      <Alert severity="success" sx={{ mb: 2 }}>Your account has been approved!</Alert>
                      <Button fullWidth variant="contained" href="/">Sign In</Button>
                    </>
                  ) : (
                    <>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <b>Account pending approval.</b><br />
                        An administrator has been notified. You'll be able to sign in once your account is approved.
                      </Alert>
                      <CircularProgress size={20} sx={{ mr: 1, verticalAlign: 'middle' }} />
                      <Typography variant="body2" color="text.secondary" component="span">
                        Checking status…
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                        You can close this page — <a href="/" style={{ color: '#1976d2' }}>sign in</a> once approved.
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              {step === 1 && !mfaDone && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                    You'll use this second factor every time you sign in.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Container>
  );
};

export default Signup;
